import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    FaArrowLeft, FaBrain, FaFileMedical, FaQuoteRight, FaCheckCircle, 
    FaSpinner, FaMicroscope, FaLanguage, FaFileAlt, FaExternalLinkAlt,
    FaDownload, FaEye, FaLock
} from 'react-icons/fa';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Nav from '../components/Nav';
import './ScientificArticleDetail.css';

const API = 'http://localhost:5000';

const ScientificArticleDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const { t, i18n } = useTranslation();

    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisStages, setAnalysisStages] = useState({});
    const [translating, setTranslating] = useState(false);
    const [activeTab, setActiveTab] = useState('summary');
    const [translation, setTranslation] = useState(null);
    const [error, setError] = useState(null);

    // PDF viewer state
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [localPdfUrl, setLocalPdfUrl] = useState(null);
    const [pdfError, setPdfError] = useState(null);
    const [showPdfViewer, setShowPdfViewer] = useState(false);

    // Translated PDF viewer state
    const [translatedPdfUrl, setTranslatedPdfUrl] = useState(null);
    const [showTranslatedViewer, setShowTranslatedViewer] = useState(false);

    const loadArticle = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/api/scientific/article/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const art = response.data;
            setArticle(art);
            // Restore already-downloaded PDF state
            if (art.localPdfPath) setLocalPdfUrl(`${API}${art.localPdfPath}`);
            // Restore already-translated state
            if (art.translatedAbstract) {
                setTranslation({ translatedAbstract: art.translatedAbstract, translatedTitle: art.title });
            }
        } catch (err) {
            console.error('Error loading article:', err);
            setError('No se pudo cargar la información del artículo.');
        } finally {
            setLoading(false);
        }
    }, [id, token]);

    useEffect(() => { loadArticle(); }, [loadArticle]);

    // ── AI Analysis (SSE) ──────────────────────────────────────
    const handleAnalyze = () => {
        setAnalyzing(true);
        setAnalysisStages({});
        const eventSource = new EventSource(
            `${API}/api/scientific/analyze-stream/${id}?targetLanguage=${i18n.language}&token=${token}`
        );

        eventSource.addEventListener('stage', (e) => {
            const { stage, status, label } = JSON.parse(e.data);
            setAnalysisStages(prev => ({ ...prev, [stage]: { status, label } }));
        });

        eventSource.addEventListener('complete', (e) => {
            const { article: updated } = JSON.parse(e.data);
            setArticle(updated);
            if (updated.translatedAbstract) {
                setTranslation(prev => ({ ...prev, translatedAbstract: updated.translatedAbstract, translatedTitle: updated.title }));
            }
            setAnalyzing(false);
            eventSource.close();
        });

        eventSource.addEventListener('error', () => {
            setError('Error durante el análisis en tiempo real.');
            setAnalyzing(false);
            eventSource.close();
        });
    };

    // ── PDF Download & Show ────────────────────────────────────
    const handleDownloadPdf = async () => {
        if (localPdfUrl) {
            setShowPdfViewer(!showPdfViewer);
            return;
        }
        setDownloadingPdf(true);
        setPdfError(null);
        try {
            const response = await axios.post(
                `${API}/api/scientific/download-pdf/${id}`, {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const fullUrl = `${API}${response.data.localUrl}`;
            setLocalPdfUrl(fullUrl);
            setShowPdfViewer(true);
        } catch (err) {
            console.error('PDF Download error:', err);
            const errorMsg = err.response?.data?.msg || 'No se pudo conectar con la fuente del PDF.';
            
            if (err.response?.data?.isLandingPage) {
                setPdfError('Este enlace es una página web de la revista, no un PDF directo. Puedes abrirlo para buscar el botón de "Full Text" o "Download PDF".');
            } else {
                setPdfError(errorMsg);
            }
        } finally {
            setDownloadingPdf(false);
        }
    };

    // ── Translation ────────────────────────────────────────────
    const handleTranslate = async () => {
        // If already translated, toggle viewer
        if (translatedPdfUrl) {
            setShowTranslatedViewer(!showTranslatedViewer);
            setActiveTab('translation');
            return;
        }
        setTranslating(true);
        try {
            const response = await axios.post(
                `${API}/api/scientific/translate-pdf/${id}`,
                { targetLang: i18n.language },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (response.data?.localUrl) {
                const fullUrl = `${API}${response.data.localUrl}`;
                console.log('[Translation] Loading translated PDF from:', fullUrl);
                setTranslatedPdfUrl(fullUrl);
                setTranslation({ ready: true });
                setActiveTab('translation');
                setShowTranslatedViewer(true);
            } else {
                throw new Error('La respuesta del servidor no contiene la URL del documento traducido.');
            }
        } catch (err) {
            console.error('Translation error:', err);
        } finally {
            setTranslating(false);
        }
    };

    if (loading) return <div className="sad-loading"><FaSpinner className="sad-spin" /> Cargando artículo...</div>;
    if (error && !article) return <div className="sad-error">{error}</div>;

    return (
        <div className="sad-page">
            <Nav />
            <div className="sad-container">

                {/* ── Header ── */}
                <header className="sad-header">
                    <div className="sad-header-left">
                        <button className="sad-back-btn" onClick={() => navigate(-1)}>
                            <FaArrowLeft /> {t('articleDetail.back')}
                        </button>
                    </div>
                </header>

                <div className="sad-main-grid">

                    <section className="sad-content-card">
                        <div className="sad-source-badge">{article.source}</div>
                        <h1 className="sad-title">{article.title}</h1>
                        <div className="sad-meta">
                            <span><strong>Revista:</strong> {article.journal}</span>
                            <span><strong>Año:</strong> {article.publicationDate}</span>
                            {article.doi && <span><strong>DOI:</strong> {article.doi}</span>}
                            {article.language && <span><strong>Idioma original:</strong> {article.language === 'en' ? '🇺🇸 Inglés' : article.language === 'es' ? '🇪🇸 Español' : article.language}</span>}
                        </div>

                        {/* ── PDF Actions (Persistent) ── */}
                        {pdfError && (
                            <div className="sad-pdf-error" style={{ marginBottom: '1.5rem' }}>
                                <FaLock /> {pdfError}
                                {(article.pdfUrl || article.url) && (
                                    <a href={article.pdfUrl || article.url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '10px' }}>
                                        Abrir en nueva pestaña <FaExternalLinkAlt />
                                    </a>
                                )}
                            </div>
                        )}

                        {/* Embedded PDF Viewer (Persistent) */}
                        {showPdfViewer && localPdfUrl && (
                            <div className="sad-pdf-viewer-container" style={{ marginBottom: '2rem' }}>
                                <iframe
                                    src={localPdfUrl}
                                    title="PDF Original"
                                    className="sad-pdf-iframe"
                                />
                            </div>
                        )}

                        {/* ── Tabs ── */}
                        <div className="sad-tabs">
                            <button className={`sad-tab-btn ${activeTab === 'summary' ? 'active' : ''}`} onClick={() => setActiveTab('summary')}>{t('articleDetail.tabs.summary')}</button>
                            <button className={`sad-tab-btn ${activeTab === 'methodology' ? 'active' : ''}`} onClick={() => setActiveTab('methodology')}>{t('articleDetail.tabs.methodology')}</button>
                            <button className={`sad-tab-btn ${activeTab === 'findings' ? 'active' : ''}`} onClick={() => setActiveTab('findings')}>{t('articleDetail.tabs.findings')}</button>
                            {translation && (
                                <button className={`sad-tab-btn ${activeTab === 'translation' ? 'active' : ''}`} onClick={() => setActiveTab('translation')}>
                                    {t('articleDetail.tabs.translation')}
                                </button>
                            )}
                        </div>

                        {/* ── Tab Content ── */}
                        <div className="sad-tab-content">

                            {/* SUMMARY */}
                            {activeTab === 'summary' && (
                                <div className="sad-abstract-section">
                                    {translation?.translatedAbstract && (
                                        <div style={{ marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '1px solid #e2e8f0' }}>
                                            <h4 style={{ color: '#FF6600', marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <FaLanguage /> Resumen (Traducido)
                                            </h4>
                                            <p className="original-text">{translation.translatedAbstract}</p>
                                        </div>
                                    )}

                                    <div>
                                        <h4 style={{ color: '#64748b', marginBottom: '0.8rem' }}>Resumen (Original)</h4>
                                        <p className="original-text">{article.abstract || 'Este artículo no tiene un resumen disponible.'}</p>
                                    </div>

                                    {article.metadata?.clinicalRelevance && (
                                        <div className="sad-clinical-box">
                                            <h4><FaCheckCircle /> {t('articleDetail.clinicalRelevance')}</h4>
                                            <p>{article.metadata.clinicalRelevance}</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* METHODOLOGY */}
                            {activeTab === 'methodology' && (
                                <div className="sad-methodology-section">
                                    {article.metadata?.methodology ? (
                                        <p>{article.metadata.methodology}</p>
                                    ) : (
                                        <div className="sad-empty-info">
                                            <FaMicroscope />
                                            <p>Realiza el análisis de IA para extraer la metodología detallada del estudio.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* FINDINGS */}
                            {activeTab === 'findings' && (
                                <div className="sad-findings-section">
                                    {article.metadata?.findings ? (
                                        <div className="sad-findings-grid">
                                            <div className="sad-finding-box">
                                                <h4>Hallazgos Principales</h4>
                                                <p>{article.metadata.findings}</p>
                                            </div>
                                            {article.metadata.limitations && (
                                                <div className="sad-finding-box limitation">
                                                    <h4>Limitaciones Detectadas</h4>
                                                    <p>{article.metadata.limitations}</p>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="sad-empty-info">
                                            <FaBrain />
                                            <p>Realiza el análisis de IA para visualizar los hallazgos y el nivel de evidencia.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* TRANSLATION TAB */}
                            {activeTab === 'translation' && translation && (
                                <div className="sad-translation-section">
                                    <div className="sad-translation-header">
                                        <h3>Traducción IA · {{
                                            es: 'Español', en: 'English', pt: 'Português'
                                        }[i18n.language] || i18n.language.toUpperCase()}</h3>
                                        {translatedPdfUrl && (
                                            <button
                                                className="sad-btn-orange-solid"
                                                onClick={() => setShowTranslatedViewer(!showTranslatedViewer)}
                                            >
                                                <FaEye /> {showTranslatedViewer ? 'Ocultar PDF Traducido' : 'Ver PDF Traducido'}
                                            </button>
                                        )}
                                    </div>

                                    {/* Translated HTML Viewer */}
                                    {showTranslatedViewer && translatedPdfUrl && (
                                        <div className="sad-pdf-viewer-container">
                                            <iframe
                                                src={translatedPdfUrl}
                                                title="PDF Traducido"
                                                className="sad-pdf-iframe"
                                            />
                                        </div>
                                    )}

                                    {/* Placeholder when not showing viewer */}
                                    {!showTranslatedViewer && (
                                        <div className="sad-translated-ready">
                                            <FaLanguage className="sad-translated-icon" />
                                            <p>Traducción completada. Si el PDF fue traducido, haz clic en <strong>Ver PDF Traducido</strong> para visualizarlo completo.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>

                    {/* ── AI Sidebar ── */}
                    <aside className="sad-ai-sidebar">
                        <div className="sad-ai-card">
                            <div className="sad-ai-header">
                                <FaBrain className="sad-ai-icon" />
                                <h3>{t('articleDetail.aiAnalysis')}</h3>
                            </div>

                            <div className="sad-ai-extra-actions" style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                                
                                {/* PDF Original Button (Only if direct PDF is available) */}
                                {(article.pdfUrl || article.localPdfPath) ? (
                                    <button
                                        className="sad-btn-orange-solid"
                                        onClick={handleDownloadPdf}
                                        disabled={downloadingPdf}
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        {downloadingPdf ? (
                                            <><FaSpinner className="sad-spin" /> Descargando...</>
                                        ) : localPdfUrl ? (
                                            <><FaEye /> {showPdfViewer ? 'Ocultar PDF Original' : 'Ver PDF Original'}</>
                                        ) : (
                                            <><FaDownload /> Descargar PDF Original</>
                                        )}
                                    </button>
                                ) : (article.url || article.doi) ? (
                                    <a
                                        href={article.url || `https://doi.org/${article.doi}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="sad-btn-orange-solid"
                                        style={{ width: '100%', justifyContent: 'center', textDecoration: 'none', display: 'flex', background: '#334155', color: '#cbd5e1', border: '1px solid #475569' }}
                                        title="El PDF no está disponible de forma pública (acceso cerrado). Puedes intentar abrir la fuente original."
                                    >
                                        <FaLock style={{ marginRight: '8px' }} /> Fuente (Acceso Cerrado)
                                    </a>
                                ) : null}

                                {/* Translate PDF Button */}
                                <button
                                    className={`sad-translate-btn orange ${translating ? 'loading' : ''}`}
                                    onClick={handleTranslate}
                                    disabled={translating || (!article.pdfUrl && !article.localPdfPath)}
                                    title={(!article.pdfUrl && !article.localPdfPath) ? "No se puede traducir el documento porque no hay un PDF público disponible." : "Traducir el documento PDF completo"}
                                    style={{ 
                                        width: '100%', 
                                        justifyContent: 'center', 
                                        opacity: (!article.pdfUrl && !article.localPdfPath) ? 0.5 : 1, 
                                        cursor: (!article.pdfUrl && !article.localPdfPath) ? 'not-allowed' : 'pointer' 
                                    }}
                                >
                                    {translating ? (
                                        <><FaSpinner className="sad-spin" /> Traduciendo PDF...</>
                                    ) : translatedPdfUrl ? (
                                        <><FaEye /> {showTranslatedViewer ? 'Ocultar PDF Traducido' : 'Ver PDF Traducido'}</>
                                    ) : (
                                        <><FaLanguage /> Traducir Documento {
                                            i18n.language === 'es' ? 'al Español' : 
                                            i18n.language === 'en' ? 'to English' : 
                                            i18n.language === 'pt' ? 'para Português' :
                                            `to ${i18n.language.toUpperCase()}`
                                        }</>
                                    )}
                                </button>
                            </div>

                            {(!article.metadata || !article.metadata.findings) && !analyzing && (
                                <div className="sad-ai-action">
                                    <p>Descubre hallazgos críticos, metodología y evidencia usando IA especializada.</p>
                                    <button className="sad-btn-analyze" onClick={handleAnalyze}>
                                        <FaMicroscope /> {t('articleDetail.analyzeBtn')}
                                    </button>
                                </div>
                            )}

                            {analyzing && (
                                <div className="sad-ai-processing">
                                    <div className="sad-progress-list">
                                        {Object.entries(analysisStages).map(([key, stage]) => (
                                            <div key={key} className={`sad-progress-item ${stage.status}`}>
                                                {stage.status === 'start' ? <FaSpinner className="sad-spin" /> : <FaCheckCircle />}
                                                <span>{stage.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                    {!analysisStages.pdf && (
                                        <div className="sad-ai-init">
                                            <FaSpinner className="sad-spin" />
                                            <p>Iniciando pipeline EcoAI...</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {article.metadata?.findings && !analyzing && (
                                <div className="sad-ai-results-mini">
                                    <div className="sad-ai-success-badge">
                                        <FaCheckCircle /> {t('articleDetail.aiSuccess')}
                                    </div>
                                    {article.picoData && (
                                        <div className="sad-pico-summary">
                                            <div className="sad-pico-tag">P: {article.picoData.population}</div>
                                            <div className="sad-pico-tag">I: {article.picoData.intervention}</div>
                                            <div className="sad-pico-tag">O: {article.picoData.outcome}</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>


                    </aside>
                </div>
            </div>
        </div>
    );
};

export default ScientificArticleDetail;
